import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import Spinner from '../../ui/Spinner';
import TargetListTable from '../../targetlist/TargetListTable';
import { usePagination } from '@/app/hooks/pagination';
import { getContactTargetLists } from '@/api/target-list';
import { getSalesforceTargetLists } from '@/api/salesforce-embed';
import { useSalesforceEmbed } from '@/app/context/salesforce-embed';
import { useParams } from 'next/navigation';
import StatusCard from '../../ui/StatusCard';
import { useTranslations } from 'next-intl';
import { Contact } from '@/types/contact';
import { ListResponse } from '@/types/common';
import { TargetList } from '@/types/targetList';

type Props = {
  contact: Contact;
};

const ContactTargetList = ({ contact }: Props) => {
  const { clientId } = useParams<{ clientId?: string }>();
  const salesforceEmbed = useSalesforceEmbed();
  const resolvedClientId = clientId || salesforceEmbed.clientId || contact?.client_id;
  const { page, limit, handlePageChange, startingAfter, endingBefore } = usePagination();
  const tTargetList = useTranslations('targetList');

  const {
    data: targetListData,
    isLoading,
    isFetching,
    refetch: refetchTargetLists,
  } = useQuery({
    queryKey: [
      'contactTargetLists',
      resolvedClientId,
      contact?.id,
      page,
      limit,
      startingAfter,
      endingBefore,
      salesforceEmbed.isEmbed,
    ],
    queryFn: () => {
      if (salesforceEmbed.isEmbed && salesforceEmbed.token) {
        return getSalesforceTargetLists({
          clientId: resolvedClientId || '',
          contactId: contact?.id,
          token: salesforceEmbed.token,
          limit,
          startingAfter,
        });
      }
      return getContactTargetLists({
        clientId: resolvedClientId || '',
        contactId: contact?.id,
        limit,
      });
    },
    enabled: !!contact?.id && !!resolvedClientId,
    refetchOnMount: 'always',
  });

  const normalizedTargetListData = useMemo<ListResponse<TargetList> | undefined>(() => {
    if (!targetListData) return undefined;
    if (Array.isArray(targetListData)) {
      const data = (targetListData as Array<TargetList & { overall_contacts_count?: number }>).map(
        item => ({
          ...item,
          contact_count: item.overall_contacts_count ?? item.contact_count ?? 0,
        })
      );
      return {
        data,
        total: data.length,
        starting_after: '',
        ending_before: '',
        has_more: false,
      };
    }
    return targetListData;
  }, [targetListData]);

  const isTargetListsLoading = isLoading || isFetching;
  const total = normalizedTargetListData?.total || 0;

  return (
    <div className="flex min-h-64 flex-col overflow-hidden">
      <div className="flex flex-col flex-1 overflow-hidden">
        {isTargetListsLoading ? (
          <div className="relative min-h-64">
            <Spinner />
          </div>
        ) : total === 0 ? (
          <div className="flex flex-col items-center justify-center h-full">
            <StatusCard
              className="border-0 !shadow-none"
              type="empty"
              heading={tTargetList('buyerNotInTargetLists')}
            />
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto">
            <TargetListTable
              isLoading={isTargetListsLoading}
              targetListData={normalizedTargetListData}
              page={page}
              limit={limit}
              handlePageChange={(page, limit) => {
                handlePageChange(
                  page,
                  normalizedTargetListData?.starting_after || '',
                  normalizedTargetListData?.ending_before || '',
                  limit
                );
              }}
              hidePagination={true}
              refetchTargetLists={refetchTargetLists}
              contactColumnsOnly
              disableRowClick={salesforceEmbed.isEmbed}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactTargetList;
